import { apiSlice } from './apiSlice';
import { ApiResponse } from '@/types';
import {
  handleOptimisticDelete,
  handleOptimisticUpdate,
  handleOptimisticCreate,
} from './optimisticUpdates';

export interface SessionResource {
  label: string;
  url: string;
  type: string;
}

export interface GalleryImage {
  url: string;
  caption?: string;
}

export interface ContentBlock {
  id: string;
  type: string;
  title?: string;
  data: any;
}

export type SessionStatus = 'Draft' | 'Published';

export interface Session {
  id: number;
  slug: string;
  title: string;
  tag: string;
  description: string;
  speaker: string;
  speakerRole: string;
  duration: string;
  date: string;
  youtubeUrl: string | null;
  thumbnailUrl: string | null;
  speakerImageUrl: string | null;
  contentBlocks: ContentBlock[];
  status: SessionStatus;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedSessionsResponse {
  count: number;
  skip: number;
  take: number;
  data: Session[];
}

export interface GetSessionsArgs {
  search?: string;
  skip?: number;
  take?: number;
  status?: SessionStatus;
  isDeleted?: boolean;
}

export type CreateSessionPayload = Omit<Session, 'id' | 'createdAt' | 'updatedAt'>;
export type UpdateSessionPayload = Partial<CreateSessionPayload>;

export const sessionsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSessions: builder.query<PaginatedSessionsResponse, GetSessionsArgs | void>({
      query: (args) => {
        let url = '/sessions';
        const params = new URLSearchParams();
        if (args) {
          if (args.search) params.append('search', args.search);
          if (args.skip !== undefined) params.append('skip', String(args.skip));
          if (args.take !== undefined) params.append('take', String(args.take));
          if (args.status) params.append('status', args.status);
          if (args.isDeleted) params.append('isDeleted', 'true');
        }
        params.append('_t', Date.now().toString());
        const qs = params.toString();
        if (qs) url += `?${qs}`;
        return url;
      },
      transformResponse: (response: ApiResponse<PaginatedSessionsResponse>) => response.data,
      providesTags: ['Session'],
    }),

    getSessionById: builder.query<Session, string>({
      query: (slug) => `/sessions/${slug}?_t=${Date.now()}`,
      transformResponse: (response: ApiResponse<Session>) => response.data,
      providesTags: (_result, _err, slug) => [{ type: 'Session', id: slug }],
    }),

    createSession: builder.mutation<Session, CreateSessionPayload>({
      query: (body) => ({ url: '/sessions', method: 'POST', body }),
      transformResponse: (response: ApiResponse<Session>) => response.data,
      invalidatesTags: ['Session'],
    }),

    updateSession: builder.mutation<Session, { id: number; body: UpdateSessionPayload }>({
      query: ({ id, body }) => ({ url: `/sessions/${id}`, method: 'PATCH', body }),
      transformResponse: (response: ApiResponse<Session>) => response.data,
      invalidatesTags: ['Session'],
    }),

    deleteSession: builder.mutation<void, number>({
      query: (id) => ({ url: `/sessions/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Session'],
    }),

    restoreSession: builder.mutation<Session, number>({
      query: (id) => ({ url: `/sessions/${id}/restore`, method: 'PATCH' }),
      transformResponse: (response: ApiResponse<Session>) => response.data,
      invalidatesTags: ['Session'],
    }),

    forceDeleteSession: builder.mutation<void, number>({
      query: (id) => ({ url: `/sessions/${id}/force`, method: 'DELETE' }),
      invalidatesTags: ['Session'],
    }),
  }),
});

export const {
  useGetSessionsQuery,
  useGetSessionByIdQuery,
  useCreateSessionMutation,
  useUpdateSessionMutation,
  useDeleteSessionMutation,
  useRestoreSessionMutation,
  useForceDeleteSessionMutation,
} = sessionsApi;
